import type { MenuContext, MenuScreen } from '../types.js';
import { createScreenShell, createMenuHeader, createCrestButton, el } from '@bn-games/ui-shell';
import { t } from '../../i18n/I18n.js';

const RUSSIAN_RULE_KEYS = [
  'setup',
  'menMovement',
  'menCaptures',
  'kings',
  'kingCaptures',
  'chain',
  'midJumpPromotion',
  'ending',
] as const;

const ENGLISH_RULE_KEYS = [
  'setup',
  'menMovement',
  'menCaptures',
  'kings',
  'kingCaptures',
  'chain',
  'ending',
] as const;

function appendRussianItems(list: HTMLElement): void {
  RUSSIAN_RULE_KEYS.forEach((key, idx) => {
    const num = `§ ${String(idx + 1).padStart(2, '0')}`;
    const title = t(`rules.russian.items.${key}.title`);
    const body = t(`rules.russian.items.${key}.body`);
    list.appendChild(
      el('article', { class: 'rule-card' }, [
        el('h3', { class: 'rule-card__heading', text: `${num} · ${title}` }),
        el('div', { class: 'rule-card__body', text: body }),
      ]),
    );
  });
}

function appendEnglishItems(list: HTMLElement): void {
  ENGLISH_RULE_KEYS.forEach((key, idx) => {
    const num = `§ ${String(idx + 1).padStart(2, '0')}`;
    const title = t(`rules.english.items.${key}.title`);
    const body = t(`rules.english.items.${key}.body`);
    list.appendChild(
      el('article', { class: 'rule-card' }, [
        el('h3', { class: 'rule-card__heading', text: `${num} · ${title}` }),
        el('div', { class: 'rule-card__body', text: body }),
      ]),
    );
  });
}

export function createRulesScreen(): MenuScreen {
  let frame: HTMLElement | null = null;

  return {
    mount(root: HTMLElement, ctx: MenuContext) {
      const shell = createScreenShell();
      frame = shell.frame;
      const body = shell.body;

      body.appendChild(createMenuHeader({ title: t('rules.screenTitle') }));

      const variant = ctx.getSettings().variant;
      const variantTitle =
        variant === 'english' ? t('rules.english.title') : t('rules.russian.title');
      body.appendChild(
        el('div', { class: 'rules-variant-title', text: variantTitle }),
      );

      const list = el('div', {
        class: 'rules',
        attrs: {
          role: 'region',
          'aria-label': `${t('rules.screenTitle')} — ${variantTitle}`,
          tabindex: '0',
        },
      });
      if (variant === 'english') {
        appendEnglishItems(list);
      } else {
        appendRussianItems(list);
      }
      body.appendChild(list);

      const back = el('div', { class: 'button-row back-button-row' }, [
        createCrestButton({ label: t('common.back'), small: true, onClick: () => ctx.navigate('menu') }),
      ]);
      body.appendChild(back);

      root.appendChild(frame);
    },
    unmount() {
      frame?.remove();
      frame = null;
    },
  };
}
